import React, { useEffect } from 'react';
import { gql } from '@apollo/client';
import { StatusBadge, colors, useGraphQLQuery } from '@tviklax/ui-components';
import { AddressCard, CompactPreview } from './styles';
import { AccountAutocomplete, AccountOption } from './AccountAutocomplete';

const MY_ACCOUNTS_MATCH_QUERY = gql`
  query MyAccountsMatch($accountType: String!, $search: String, $limit: Int) {
    myAccounts(accountType: $accountType, search: $search, limit: $limit) {
      items { id name email phone country }
    }
  }
`;

interface ExtractedParty {
  name: string;
  email: string;
  addressLine1?: string;
  city?: string;
  country?: string;
}

interface PartyMatchCardProps {
  title: string;
  accountType: 'vendor' | 'customer';
  fieldPrefix: 'Vendor' | 'Customer';
  extracted: ExtractedParty;
  /** True when this side is the caller's own pinned identity — the backend
   * ignores whatever name/email was extracted for this side and always
   * resolves it to the caller's own account (see order_service.create_order),
   * so the extracted text is never the real answer here. */
  isSelf: boolean;
  selfIdentity?: { name: string; email?: string };
  onResolved: (fields: Record<string, string>) => void;
}

// Extraction from an imported invoice can grab the wrong party entirely
// (e.g. picking up the platform's own letterhead as the "vendor", or a
// signatory's personal name as the "customer") — this card cross-checks the
// extracted name/email against the caller's actual trade-partner roster
// (see the Accounts page / myAccounts) instead of trusting raw OCR text, so
// "Confirmed" means "matches an account you actually know," not just
// "a string was extracted."
export const PartyMatchCard: React.FC<PartyMatchCardProps> = ({
  title,
  accountType,
  fieldPrefix,
  extracted,
  isSelf,
  selfIdentity,
  onResolved,
}) => {
  const nameKey = `${fieldPrefix}Name`;
  const emailKey = `${fieldPrefix}Email`;
  const phoneKey = `${fieldPrefix}ContactNumber`;
  const countryKey = fieldPrefix === 'Vendor' ? 'vendorCountry' : 'customerCountry';

  useEffect(() => {
    if (!isSelf || !selfIdentity) return;
    const fields: Record<string, string> = {};
    if (selfIdentity.name && selfIdentity.name !== extracted.name) fields[nameKey] = selfIdentity.name;
    if (selfIdentity.email && selfIdentity.email !== extracted.email) fields[emailKey] = selfIdentity.email;
    if (Object.keys(fields).length > 0) onResolved(fields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelf, selfIdentity?.name, selfIdentity?.email]);

  const searchTerm = extracted.email || extracted.name;
  const { data, loading } = useGraphQLQuery<{ myAccounts: { items: AccountOption[] } }>(MY_ACCOUNTS_MATCH_QUERY, {
    showErrorToast: false,
    variables: { accountType, search: searchTerm || null, limit: 5 },
    skip: isSelf || !searchTerm,
  });

  const items = data?.myAccounts.items ?? [];
  const match = items.find((item) =>
    (!!extracted.email && item.email?.toLowerCase() === extracted.email.toLowerCase()) ||
    item.name.toLowerCase() === extracted.name.toLowerCase()
  );

  useEffect(() => {
    if (isSelf || !match) return;
    onResolved({
      [nameKey]: match.name,
      ...(match.email ? { [emailKey]: match.email } : {}),
      ...(match.phone ? { [phoneKey]: match.phone } : {}),
      ...(match.country ? { [countryKey]: match.country } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelf, match?.id]);

  const cardStyle = { display: 'flex', flexDirection: 'column' as const, gap: '1rem', gridColumn: 'span 1' };
  const headerStyle = { display: 'flex', justifyContent: 'space-between' as const, alignItems: 'center' as const };
  const titleStyle = { fontSize: '14px', fontWeight: 600, color: '#475569' };

  if (isSelf) {
    const name = selfIdentity?.name || extracted.name;
    const email = selfIdentity?.email || extracted.email;
    return (
      <AddressCard style={cardStyle}>
        <div style={headerStyle}>
          <h4 style={titleStyle}>{title}</h4>
          <StatusBadge size="sm" variant="success" label="Confirmed" />
        </div>
        <CompactPreview>
          <strong>{name || 'Name Missing'}</strong>
          <p>{extracted.addressLine1}</p>
          <p>{[extracted.city, extracted.country].filter(Boolean).join(', ')}</p>
          <p>{email}</p>
        </CompactPreview>
      </AddressCard>
    );
  }

  if (loading) {
    return (
      <AddressCard style={cardStyle}>
        <div style={headerStyle}>
          <h4 style={titleStyle}>{title}</h4>
          <StatusBadge size="sm" variant="neutral" label="Checking…" />
        </div>
        <CompactPreview>Checking your roster for a match…</CompactPreview>
      </AddressCard>
    );
  }

  if (match) {
    return (
      <AddressCard style={cardStyle}>
        <div style={headerStyle}>
          <h4 style={titleStyle}>{title}</h4>
          <StatusBadge size="sm" variant="success" label="Confirmed" />
        </div>
        <CompactPreview>
          <strong>{match.name}</strong>
          <p>{extracted.addressLine1}</p>
          <p>{[extracted.city, match.country || extracted.country].filter(Boolean).join(', ')}</p>
          <p>{match.email}</p>
        </CompactPreview>
      </AddressCard>
    );
  }

  return (
    <AddressCard style={cardStyle}>
      <div style={headerStyle}>
        <h4 style={titleStyle}>{title}</h4>
        <StatusBadge size="sm" variant="pending" label="New — not on your roster" />
      </div>
      <AccountAutocomplete
        accountType={accountType}
        label={`${fieldPrefix} Name`}
        name={nameKey}
        value={extracted.name}
        onChange={(e) => onResolved({ [nameKey]: e.target.value })}
        onAccountSelected={(account: AccountOption) => onResolved({
          [nameKey]: account.name,
          ...(account.email ? { [emailKey]: account.email } : {}),
          ...(account.phone ? { [phoneKey]: account.phone } : {}),
          ...(account.country ? { [countryKey]: account.country } : {}),
        })}
        placeholder="Search your roster, or leave as-is to add as new"
      />
      <div style={{ fontSize: 12, color: colors.gray500 }}>
        {extracted.email}
        <br />
        This doesn't match anyone on your roster yet — it'll be added as a new {accountType === 'vendor' ? 'supplier' : 'customer'} when the order is created.
      </div>
    </AddressCard>
  );
};
